import { FC, RefObject, useEffect, useRef, useState } from "react";
import { useData } from "data/data-provider";
import DbIcon from "designs/DesignB/components/icons";
import CarDetailsBody, { FEATURES_SHOWN } from "./CarDetailsBody";
import { CarCta, CarPrice } from "./CarCta";
import SwipeGallery from "./SwipeGallery";
import { descriptionText, detailSpecs, featureNames, galleryPhotos } from "./carDetails";

export interface CarDetailPanelProps {
  car: any;
  /** The panel's id, which the open card's button names in aria-controls; none while it closes. */
  id?: string;
  titleId?: string;
  className?: string;
  onClose: () => void;
  onBook: () => void;
}

/**
 * Where the price bar goes while the panel runs past the bottom of the screen: fixed to the foot
 * of the screen, over the panel's own width. CSS sticky cannot do it here: #root is
 * `overflow: hidden` (public/index.html), which turns sticky off for the whole site. The bar's
 * own slot keeps its height, so the panel does not jump when the bar lifts out of it.
 */
const usePinnedFoot = (panel: RefObject<HTMLElement>, slot: RefObject<HTMLElement>) => {
  const [pin, setPin] = useState<{ left: number; width: number; height: number } | null>(null);
  useEffect(() => {
    let raf = 0;
    const check = () => {
      raf = 0;
      const p = panel.current;
      const s = slot.current;
      if (!p || !s) return;
      const box = p.getBoundingClientRect();
      const bar = s.getBoundingClientRect();
      const vh = window.innerHeight;
      // Only once the panel has settled: a transformed panel would carry the bar. A class check, not
      // getComputedStyle, so this costs no style recalc on every scroll frame.
      const on = bar.bottom > vh + 1 && box.top < vh - bar.height * 2 && !!p.closest(".db-xp.is-settled");
      setPin((cur) =>
        !on
          ? null
          : cur && cur.left === box.left && cur.width === box.width && cur.height === bar.height
          ? cur
          : { left: box.left, width: box.width, height: bar.height },
      );
    };
    const later = () => {
      if (!raf) raf = requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", later, { passive: true });
    window.addEventListener("resize", later);
    const el = panel.current;
    el?.addEventListener("transitionend", later);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(later) : null;
    if (el) ro?.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", later);
      window.removeEventListener("resize", later);
      el?.removeEventListener("transitionend", later);
      ro?.disconnect();
    };
  }, [panel, slot]);
  return pin;
};

/**
 * The car's details, opened in place under its row (1NOW-11219, mockup inline-expand option A):
 * the photos on the left, the name and the spec sheet on the right, the features full width
 * under both, and the price with Book now pinned to the foot of the screen while the panel is on
 * it. Book now is here and not on the card: the card opens this.
 */
const CarDetailPanel: FC<CarDetailPanelProps> = ({ car, id, titleId, className = "", onClose, onBook }) => {
  const { ourCompany } = useData();
  const specs = detailSpecs(car, ourCompany);
  const description = descriptionText(car?.description);
  const features = featureNames(car);
  const long = (description?.length ?? 0) > 280 || features.length > FEATURES_SHOWN;
  const panel = useRef<HTMLElement>(null);
  const slot = useRef<HTMLDivElement>(null);
  const pin = usePinnedFoot(panel, slot);

  return (
    <section
      ref={panel}
      className={`db-xp-panel ${className}`}
      id={id}
      role="region"
      tabIndex={-1}
      aria-labelledby={titleId}
    >
      <div className="db-xp-main">
        <div className="db-xp-top">
          <h3 className="db-xp-name" id={titleId} title={car?.name}>
            {car?.name}
          </h3>
          <button type="button" className="db-xp-x" aria-label="Close details" onClick={onClose}>
            <DbIcon name="close" className="h-[22px] w-[22px]" />
          </button>
        </div>
        <div className="db-xp-media">
          <SwipeGallery photos={galleryPhotos(car)} name={car?.name ?? "this car"} />
        </div>
        <div className="db-xp-body">
          <CarDetailsBody part="main" specs={specs} description={description} features={features} />
        </div>
        <CarDetailsBody part="features" specs={specs} description={description} features={features} />
        {long && (
          <div className="db-xp-end">
            <button type="button" className="db-xp-hide" onClick={onClose}>
              Hide details
              <DbIcon name="chevronDown" className="h-4 w-4 rotate-180" />
            </button>
          </div>
        )}
      </div>
      <div ref={slot} style={pin ? { height: pin.height } : undefined}>
        <div
          className={`db-xp-foot ${pin ? "is-pinned" : ""}`}
          style={pin ? { left: pin.left, width: pin.width } : undefined}
        >
          <CarPrice car={car} />
          <CarCta car={car} onBook={onBook} />
        </div>
      </div>
    </section>
  );
};

export default CarDetailPanel;
